import { createRouter, createWebHistory } from 'vue-router';
import Login from '../views/Login.vue';
import ForgotPassword from '../views/ForgotPassword.vue';
import CreatePassword from '../views/CreatePassword.vue';
import AdminLayout from '../views/AdminLayout.vue';
import AdminDashboard from '../views/AdminDashboard.vue';
import AdminOrders from '../views/AdminOrders.vue';
import AdminManage from '../views/AdminManage.vue';
import AdminProducts from '../views/AdminProducts.vue';
import AdminInventory from '../views/AdminInventory.vue';
import AdminStatistics from '../views/AdminStatistics.vue';
import ProductList from '../views/Products.vue';
import Help from '../views/Help.vue';
import Manage from '../views/Manage.vue';
import NewOrder from '../views/NewOrder.vue';
import BuildOrder from '../views/BuildOrder.vue';
import OrdersList from '../views/OrdersList.vue';
import Clients from '../views/Clients.vue';
import Companies from '../views/Companies.vue';
import Suppliers from '../views/Suppliers.vue';
import Printers from '../views/Printers.vue';
import Tickets from '../views/Tickets.vue';
import AddProduct from '../views/AddProduct.vue';
import Categories from '../views/Categories.vue';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, getUserRole, getIdTokenClaims } from '../firebase';
const routes = [
    {
        path: '/',
        name: 'Login',
        component: Login,
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: ForgotPassword,
    },
    {
        path: '/create-password',
        name: 'CreatePassword',
        component: CreatePassword,
    },
    {
        path: '/app',
        component: AdminLayout,
        meta: { requiresAuth: true, roles: ['admin', 'client'] },
        children: [
            { path: 'dashboard', name: 'Dashboard', component: AdminDashboard },
            { path: 'orders', component: AdminOrders, children: [
                    { path: '', name: 'OrdersList', component: OrdersList, meta: { roles: ['admin'] } },
                    { path: 'new', name: 'NewOrder', component: NewOrder },
                    { path: 'build', name: 'BuildOrder', component: BuildOrder, meta: { roles: ['admin'] } },
                ]
            },
            { path: 'products', component: AdminProducts, meta: { roles: ['admin'] }, children: [
                    { path: '', name: 'Products', component: ProductList },
                    { path: 'add', name: 'AddProduct', component: AddProduct },
                    { path: 'categories', name: 'Categories', component: Categories },
                ]
            },
            { path: 'inventory', name: 'Inventory', component: AdminInventory, meta: { roles: ['admin'] } },
            { path: 'statistics', name: 'Statistics', component: AdminStatistics, meta: { roles: ['admin'] } },
            { path: 'help', name: 'Help', component: Help },
            {
                path: 'manage',
                component: AdminManage,
                meta: { roles: ['admin'] },
                children: [
                    { path: '', name: 'Manage', component: Manage },
                    { path: 'clients', name: 'Clients', component: Clients },
                    { path: 'companies', name: 'Companies', component: Companies },
                    { path: 'suppliers', name: 'Suppliers', component: Suppliers },
                    { path: 'printers', name: 'Printers', component: Printers },
                    { path: 'tickets', name: 'Tickets', component: Tickets },
                ]
            },
            {
                path: '/users/:id',
                name: 'UserProfile',
                component: () => import('@/views/UserProfilePage.vue'),
                props: true
            },
        ]
    },
];
const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});
function getCurrentUser(timeout = 3000) {
    // fast-path if auth already has a currentUser
    const current = auth.currentUser;
    if (current)
        return Promise.resolve(current);
    return new Promise((resolve) => {
        const unsub = onAuthStateChanged(auth, (user) => {
            unsub();
            resolve(user);
        });
        // fallback in case onAuthStateChanged doesn't fire quickly
        setTimeout(() => {
            try {
                unsub();
            }
            catch { }
            resolve(auth.currentUser);
        }, timeout);
    });
}
router.beforeEach(async (to, from, next) => {
    // The deepest route may narrow the access granted by its parent.
    const requiredRoles = [...to.matched]
        .reverse()
        .find(record => Array.isArray(record.meta.roles))?.meta.roles;
    if (!requiredRoles)
        return next();
    const user = await getCurrentUser();
    if (!user) {
        console.debug('[router.beforeEach] not authenticated, redirect to /');
        return next({ path: '/' });
    }
    console.debug('[router.beforeEach] checking roles for uid=', user.uid, 'required=', requiredRoles);
    try {
        await getIdTokenClaims().catch(() => { });
        const role = await getUserRole(user.uid);
        console.debug('[router.beforeEach] fetched role=', role, 'for uid=', user.uid);
        if (!role) {
            console.warn('[router.beforeEach] no role found, redirecting', user.uid);
            return next({ path: '/' });
        }
        if (requiredRoles.includes(role) || requiredRoles.includes('anyAuthenticated')) {
            return next();
        }
        console.warn('[router.beforeEach] role mismatch. userRole=', role, 'required=', requiredRoles);
        return next({ path: '/app/dashboard' });
    }
    catch (err) {
        console.error('[router.beforeEach] Role check failed', 'uid=', user.uid, 'code=', err?.code, 'message=', err?.message, err);
        // permission-denied most likely
        return next({ path: '/' });
    }
});
export default router;
